import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Trash2, Upload, CheckCircle2, XCircle } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { getOrderItemMediaUrlFallback, resolveOrderItemMediaUrl } from '@/lib/order-item-media'
import { getErrorMessage } from '@/lib/api'
import {
  USER_DOCUMENT_STATUS,
  userDocumentsApi,
  type UserDocSectionCatalogEntry,
  type UserDocumentRecord,
} from '@/services/user-documents'
import { useAuthStore } from '@/stores/auth-store'

const acceptAttr = 'image/*,.pdf,application/pdf'

type UserDocumentsPanelProps = {
  targetUserId?: string | number | null | undefined
  orgInstanceId?: string | number | null | undefined
  userGroupId?: string | number | null | undefined
  allowVerifyControls?: boolean
}

function isDocumentApproved(doc?: UserDocumentRecord | null) {
  if (!doc) {
    return false
  }
  return Boolean(doc.is_verified) || doc.status === USER_DOCUMENT_STATUS.APPROVED
}

function resolveStatusMeta(doc?: UserDocumentRecord | null) {
  if (!doc) {
    return {
      label: 'Not uploaded',
      className: 'border-slate-200 bg-slate-50 text-slate-700',
    }
  }

  if (isDocumentApproved(doc)) {
    return {
      label: 'Approved',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    }
  }

  if (doc.status === USER_DOCUMENT_STATUS.REJECTED) {
    return {
      label: 'Rejected',
      className: 'border-red-200 bg-red-50 text-red-800',
    }
  }

  return {
    label: doc.status_label?.trim() || 'Pending approval',
    className: 'border-amber-200 bg-amber-50 text-amber-800',
  }
}

function SlotPreview({ label, url }: { label: string; url?: string | null }) {
  const resolvedUrl = url ? (resolveOrderItemMediaUrl(url) ?? url) : null
  const [imageSrc, setImageSrc] = useState<string | null>(resolvedUrl)
  const [loadFailed, setLoadFailed] = useState(false)

  useEffect(() => {
    setImageSrc(resolvedUrl)
    setLoadFailed(false)
  }, [resolvedUrl])

  if (!resolvedUrl) {
    return (
      <div className="flex h-36 items-center justify-center rounded-lg border border-dashed bg-muted/30 text-center text-sm text-muted-foreground">
        No {label} uploaded
      </div>
    )
  }
  const isPdf = /\.pdf(?:\b|$)/i.test(resolvedUrl) || /\bpdf\b/i.test((resolvedUrl.split('?')[0] ?? ''))
  if (isPdf) {
    return (
      <a
        href={resolvedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex h-36 items-center justify-center gap-2 rounded-lg border bg-muted/20 font-medium text-primary hover:bg-muted"
      >
        <FileText className="h-8 w-8" /> PDF · {label}
      </a>
    )
  }

  if (loadFailed) {
    return (
      <a
        href={resolvedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex h-36 items-center justify-center rounded-lg border bg-muted/20 text-sm font-medium text-primary hover:bg-muted"
      >
        Open {label} preview
      </a>
    )
  }

  return (
    <img
      alt={label}
      src={imageSrc ?? undefined}
      className="h-36 w-full rounded-lg border object-cover"
      onError={() => {
        const fallback = getOrderItemMediaUrlFallback(resolvedUrl)
        if (fallback && fallback !== imageSrc) {
          setImageSrc(fallback)
          return
        }
        setLoadFailed(true)
      }}
    />
  )
}

type DocumentSlotProps = {
  label: 'front' | 'back'
  url?: string | null
  editable: boolean
  busy: boolean
  onUpload: (file: File) => void
  onRemove?: () => void
}

function DocumentSlot({ label, url, editable, busy, onUpload, onRemove }: DocumentSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium capitalize">{label}</p>
        {url && editable ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-destructive hover:text-destructive"
            disabled={busy}
            onClick={onRemove}
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            Remove
          </Button>
        ) : null}
      </div>
      <SlotPreview label={label} url={url} />
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        disabled={!editable || busy}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) {
            onUpload(file)
          }
          e.target.value = ''
        }}
      />
      {editable ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="w-full"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="mr-2 h-4 w-4" />
          {url ? `Replace ${label}` : `Upload ${label}`}
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">Approved documents cannot be changed.</p>
      )}
    </div>
  )
}

function canReviewDocumentsFromCapabilities(
  capabilities?: {
    can_verify_user_documents?: boolean
    can_manage_user_lifecycle?: boolean
    can_assign_user_roles?: boolean
    can_access_user_directory?: boolean
    can_access_user_documents?: boolean
    can_manage_user_documents?: boolean
  } | null,
) {
  return Boolean(
    capabilities?.can_verify_user_documents
    || capabilities?.can_manage_user_documents
    || capabilities?.can_manage_user_lifecycle
    || capabilities?.can_assign_user_roles
    || capabilities?.can_access_user_directory
    || capabilities?.can_access_user_documents,
  )
}

export function UserDocumentsPanel({
  targetUserId,
  orgInstanceId,
  userGroupId,
  allowVerifyControls = false,
}: UserDocumentsPanelProps) {
  const queryClient = useQueryClient()
  const currentUserId = useAuthStore((state) => state.user?.id)
  const currentCapabilities = useAuthStore((state) => state.currentPost?.capabilities)
  const targetId =
    targetUserId == null || targetUserId === '' ? null : targetUserId
  const isReviewingOtherUser = Boolean(
    targetId
    && currentUserId
    && String(targetId) !== String(currentUserId),
  )
  const canReviewDocuments = allowVerifyControls
    || (isReviewingOtherUser && canReviewDocumentsFromCapabilities(currentCapabilities))
  const canManageDocuments = !isReviewingOtherUser || canReviewDocuments
  const scopedOrgInstanceId =
    orgInstanceId == null || orgInstanceId === '' ? null : orgInstanceId
  const scopedUserGroupId =
    userGroupId == null || userGroupId === '' ? null : userGroupId

  const catalogQuery = useQuery({
    queryKey: ['user-documents-catalog', targetId ?? '__none', scopedOrgInstanceId ?? '__none', scopedUserGroupId ?? '__none'],
    queryFn: () => userDocumentsApi.listCatalog({
      orgInstance: scopedOrgInstanceId,
      userGroupId: scopedUserGroupId,
      userId: isReviewingOtherUser ? targetId : undefined,
    }),
    enabled: Boolean(targetId),
  })

  const fallbackSectionsQuery = useQuery({
    queryKey: ['user-documents-catalog-fallback-sections', scopedOrgInstanceId ?? '__none', scopedUserGroupId ?? '__none'],
    queryFn: async () => {
      const sections = await userDocumentsApi.listSections({
        orgInstance: scopedOrgInstanceId as string | number,
        userGroupId: scopedUserGroupId,
      })
      return sections.map((section) => ({
        id: section.id,
        document_name: section.document_name,
        slug: section.slug,
      }))
    },
    enabled: Boolean(targetId) && Boolean(scopedOrgInstanceId) && !catalogQuery.isLoading && (catalogQuery.data?.length ?? 0) === 0,
  })

  const docsQuery = useQuery({
    queryKey: ['user-documents-rows', targetId],
    queryFn: () => userDocumentsApi.listForUser(targetId as string | number),
    enabled: Boolean(targetId),
  })

  const bySection = useMemo(() => {
    const m = new Map<number, UserDocumentRecord>()
    for (const doc of docsQuery.data ?? []) {
      if (doc.doc_section != null) {
        m.set(doc.doc_section, doc)
      }
    }
    return m
  }, [docsQuery.data])

  const invalidateDocs = async () => {
    await queryClient.invalidateQueries({ queryKey: ['user-documents-rows', targetId] })
  }

  const uploadMutation = useMutation({
    mutationFn: async ({
      section,
      slot,
      file,
    }: {
      section: UserDocSectionCatalogEntry
      slot: 'front' | 'back'
      file: File
    }) => {
      if (!targetId) {
        throw new Error('Missing user id')
      }
      const existing = bySection.get(section.id)
      if (existing?.id) {
        return userDocumentsApi.patch(existing.id, {
          documentFront: slot === 'front' ? file : undefined,
          documentBack: slot === 'back' ? file : undefined,
        })
      }
      return userDocumentsApi.create({
        docSectionId: section.id,
        userId: targetId,
        documentFront: slot === 'front' ? file : undefined,
        documentBack: slot === 'back' ? file : undefined,
      })
    },
    onSuccess: async () => {
      await invalidateDocs()
      toast.success('Document saved')
    },
    onError: (e) => {
      toast.error(getErrorMessage(e))
    },
  })

  const clearSlotMutation = useMutation({
    mutationFn: async ({ docId, slot }: { docId: number; slot: 'front' | 'back' }) => (
      slot === 'front'
        ? userDocumentsApi.removeFront(docId)
        : userDocumentsApi.removeBack(docId)
    ),
    onSuccess: async () => {
      await invalidateDocs()
      toast.success('Document removed')
    },
    onError: (e) => {
      toast.error(getErrorMessage(e))
    },
  })

  const removeDocumentMutation = useMutation({
    mutationFn: async (docId: number) => userDocumentsApi.remove(docId),
    onSuccess: async () => {
      await invalidateDocs()
      toast.success('Document removed')
    },
    onError: (e) => {
      toast.error(getErrorMessage(e))
    },
  })

  const verifyMutation = useMutation({
    mutationFn: async ({ docId, action }: { docId: number; action: 'approve' | 'reject' }) => (
      action === 'approve'
        ? userDocumentsApi.approve(docId)
        : userDocumentsApi.reject(docId)
    ),
    onSuccess: async (_data, variables) => {
      await invalidateDocs()
      toast.success(variables.action === 'approve' ? 'Document approved' : 'Document rejected')
    },
    onError: (e) => {
      toast.error(getErrorMessage(e))
    },
  })

  const isBusy =
    uploadMutation.isPending
    || clearSlotMutation.isPending
    || removeDocumentMutation.isPending
    || verifyMutation.isPending

  if (!targetId) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        User id is not available.
      </div>
    )
  }

  if (catalogQuery.isLoading || docsQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading documents…</p>
  }

  if (catalogQuery.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load document catalogue:
        {' '}
        {getErrorMessage(catalogQuery.error)}
      </p>
    )
  }

  const sections = (catalogQuery.data?.length ?? 0) > 0
    ? (catalogQuery.data ?? [])
    : (fallbackSectionsQuery.data ?? [])

  if (sections.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        No document sections are configured for your profile yet.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {isReviewingOtherUser
          ? 'Upload, replace, or review documents on behalf of this user. Files can be changed until approved.'
          : 'Upload front and back copies of each required document. You can remove or replace files until they are approved.'}
      </p>
      {!canManageDocuments && isReviewingOtherUser ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          You can view this user&apos;s documents, but your role cannot upload or change them.
        </p>
      ) : null}
      {sections.map((section) => {
        const doc = bySection.get(section.id)
        const statusMeta = resolveStatusMeta(doc)
        const editable = canManageDocuments && !isDocumentApproved(doc)
        const hasAnyFile = Boolean(doc?.document_front || doc?.document_back)
        const isApproved = isDocumentApproved(doc)
        const isRejected = doc?.status === USER_DOCUMENT_STATUS.REJECTED
        const showVerifyControls = canReviewDocuments && Boolean(doc?.id) && hasAnyFile

        return (
          <Card key={section.id} className="border-border/70">
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base">{section.document_name}</CardTitle>
                  <CardDescription>
                    {section.slug ? `Code: ${section.slug}` : `Section #${section.id}`}
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={cn('font-medium', statusMeta.className)}>
                    {statusMeta.label}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <DocumentSlot
                  label="front"
                  url={doc?.document_front}
                  editable={editable}
                  busy={isBusy}
                  onUpload={(file) => uploadMutation.mutate({ section, slot: 'front', file })}
                  onRemove={
                    doc?.id && doc.document_front
                      ? () => clearSlotMutation.mutate({ docId: doc.id, slot: 'front' })
                      : undefined
                  }
                />
                <DocumentSlot
                  label="back"
                  url={doc?.document_back}
                  editable={editable}
                  busy={isBusy}
                  onUpload={(file) => uploadMutation.mutate({ section, slot: 'back', file })}
                  onRemove={
                    doc?.id && doc.document_back
                      ? () => clearSlotMutation.mutate({ docId: doc.id, slot: 'back' })
                      : undefined
                  }
                />
              </div>

              {showVerifyControls ? (
                <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
                  <Button
                    type="button"
                    size="sm"
                    variant={isApproved ? 'default' : 'outline'}
                    disabled={verifyMutation.isPending || isApproved}
                    onClick={() => verifyMutation.mutate({ docId: doc!.id, action: 'approve' })}
                  >
                    <CheckCircle2 className="mr-1.5 h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={isRejected ? 'destructive' : 'outline'}
                    disabled={verifyMutation.isPending || isRejected}
                    onClick={() => verifyMutation.mutate({ docId: doc!.id, action: 'reject' })}
                  >
                    <XCircle className="mr-1.5 h-4 w-4" />
                    Reject
                  </Button>
                </div>
              ) : null}

              {editable && doc?.id && hasAnyFile ? (
                <div className="flex justify-end border-t pt-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    disabled={isBusy}
                    onClick={() => removeDocumentMutation.mutate(doc.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Remove document
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
